// Global Types
import type {
  $Translate,
} from 'common.language';
import type {
  $FullRegattaLog,
} from 'common.data';

// Global Enums
import {
  adminPages,
  adminSections,
} from 'common.enums';

// Global Components
import * as React from 'react';
import {
  Button,
  Card,
  CardBody,
  Table,
} from 'reactstrap';
import ReactDiffViewer from 'react-diff-viewer';
import {
  CommonDialog,
  Loader,
  SectionHeader,
  TdDate,
} from '@vroskus/library-components';

// Helpers
import _ from 'lodash';
import {
  isDiff,
} from '@vroskus/library-helpers/dist/browser';
import {
  regattaLogActionLabel,
} from 'common.language';
import {
  defaultGetData,
} from '../../../helpers';

// Components
import Page from '../../layout/Page';
import EmptyTable from '../../common/EmptyTable';
import ThQty from '../../common/ThQty';

// Services
import Services from '../../../services';

// Enums
import {
  regattaLogActionClass,
} from '../../../enums';

// Types
import type {
  $ReducerState,
} from '../../../redux';
import type {
  $RedirectAction,
} from '../../../redux/modules/main/actions';

export type $OwnProps = $Translate;

export type $StateProps = {
  main: $ReducerState<'main'>;
};

export type $DispatchProps = {
  redirect: $RedirectAction;
};

type $Props = $DispatchProps & $OwnProps & $StateProps;

type $State = {
  data: Array<$FullRegattaLog>;
  loading: boolean;
};

const pageId = adminPages.regattaLogsPage;

class RegattaLogsPageView extends React.Component<$Props, $State> {
  constructor(props: $Props) {
    super(props);

    this.state = {
      data: [],
      loading: false,
    };
  }

  componentDidMount(): void {
    const {
      main,
      redirect,
    } = this.props;

    if (main.activeRegattaId === null) {
      redirect({
        pathname: '/regattas',
      });
    }

    this.getData();
    this.setupSocket();
  }

  componentDidUpdate(prevProps: $Props): void {
    const {
      main,
    } = this.props;

    if (main.activeRegattaId !== null && isDiff(
      main.activeRegattaId,
      prevProps.main.activeRegattaId,
    )) {
      this.getData();
    }
  }

  setupSocket(): void {
    Services.Socket.on({
      action: (data) => {
        const {
          main,
        } = this.props;

        if (main.activeRegattaId === data.RegattaId && data.modelName === 'RegattaLog') {
          this.getData();
        }
      },
      type: Services.Socket.type.modelChangedEvent,
    });
  }

  setStateAsync(state: Partial<$State>): Promise<void> {
    return new Promise((resolve) => {
      this.setState(
        (pervState: $State) => ({
          ...pervState,
          ...state,
        }),
        resolve,
      );
    });
  }

  async getData(): Promise<void> {
    const {
      main,
    } = this.props;

    const RegattaId = main.activeRegattaId;

    await defaultGetData({
      action: async () => {
        if (RegattaId !== null) {
          await this.setStateAsync({
            loading: true,
          });

          const data = await Services.Api.getManyRegattaLog({
            RegattaId,
          });

          await this.setStateAsync({
            data,
            loading: false,
          });
        }
      },
      errorAction: async () => this.setStateAsync({
        loading: false,
      }),
    });
  }

  renderHeader() {
    const {
      main,
      t,
    } = this.props;

    const RegattaId = main.activeRegattaId;

    if (RegattaId !== null) {
      return (
        <Card id={adminSections[pageId].header}>
          <SectionHeader
            iconClass={'icon-folder-alt'}
            title={t('Regatta logs')}
          />
        </Card>
      );
    }

    return null;
  }

  renderButtons(dataItem: $FullRegattaLog) {
    const {
      t,
    } = this.props;

    return (
      <div className={'d-flex justify-content-end'}>
        <CommonDialog
          content={(
            <CardBody>
              <ReactDiffViewer
                newValue={JSON.stringify(
                  _.get(
                    dataItem,
                    'Changes.next',
                  ) || '',
                  undefined,
                  2,
                )}
                oldValue={JSON.stringify(
                  _.get(
                    dataItem,
                    'Changes.prev',
                  ) || '',
                  undefined,
                  2,
                )}
              />
            </CardBody>
          )}
          id={`regatta-log-dialog-${dataItem.id}`}
          labelCloseButton={t('Close')}
          labelConfirmButton={t('Confirm')}
          size={'xl'}
          title={t('Diff')}
        >
          <Button
            color={'secondary'}
            id={`regatta-log-button-${dataItem.id}`}
            outline
            size={'sm'}
          >
            {t('View')}
          </Button>
        </CommonDialog>
      </div>
    );
  }

  renderTable() {
    const {
      t,
    } = this.props;
    const {
      data,
    } = this.state;

    return data.length === 0 ? (
      <EmptyTable className={'mt-5'} />
    ) : (
      <Table responsive>
        <thead>
          <tr>
            <th>{t('Action')}</th>
            <th>{t('Model')}</th>
            <th>{t('Admin')}</th>
            <th>{t('Date created')}</th>
            <ThQty value={data.length} />
          </tr>
        </thead>
        <tbody>
          {data.map((dataItem: $FullRegattaLog) => (
            <tr key={dataItem.id}>
              <td>
                <span className={`badge rounded-pill ${regattaLogActionClass[dataItem.Action]}`}>
                  {t(regattaLogActionLabel[dataItem.Action])}
                </span>
              </td>
              <td className={'lh-1 py-1'}>
                <div>
                  {dataItem.ModelName}
                </div>
                <small className={'text-muted'}>
                  {dataItem.ModelContent}
                </small>
              </td>
              <td>
                {dataItem.Admin !== null ? dataItem.Admin.Name : ''}
              </td>
              <TdDate
                duration
                multiple
                value={dataItem.createdAt}
              />
              <td>
                {this.renderButtons(dataItem)}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  }

  renderContent() {
    const {
      loading,
    } = this.state;

    return (
      <Card id={adminSections[pageId].list}>
        <Loader
          loading={loading === true}
          overlay
        >
          {this.renderTable()}
        </Loader>
      </Card>
    );
  }

  render() {
    return (
      <Page id={pageId}>
        <>
          {this.renderHeader()}
          {this.renderContent()}
        </>
      </Page>
    );
  }
}

export default RegattaLogsPageView;
